import { Component, Mixins, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import {
    deviceChange,
    FeatureEnabledV2,
    formatCurrency,
    getFeatureEnabled,
    getFeatureValueV2,
    isBreakpointDesktopQuery,
    isBreakpointLargeQuery,
    isMobileQuery,
    languageQuery,
    MagicConfigMixin,
    scroll,
    Throttle,
} from '@gig/magic-common';

import { config, FAST_TRACK_INITIALIZED } from '../../services';
import type { ITopNavigationOptions } from '../../services';
import { RouteName } from '../../routes/routes.constants';
import type { ISubscription } from '@gig/magic-message-bus/dist/base-bus';
import {
    Feature,
    LANGUAGE_UPDATED,
    MESSAGING_LOADED,
    MESSAGING_UNREAD_LIST,
    BREAKPOINT_LANDSCAPE_UPDATED,
    REWARD_COUNT,
    USER_LOGGED_IN_UPDATED,
    SINGLE_LANGUAGE_QUERY,
    PANIC_BAR_VISIBLE,
    FeatureSettings,
    IS_MOBILE_APP_QUERY,
} from '@gig/magic-messages';
import { OmitFirst } from '../../types';
import { isTopNavEnabled, openProperLiveChat } from '../../util';
import { namespace } from 'vuex-class';
import {
    NAVIGATIONS_NAMESPACE,
    getters,
    actions,
    isTopNav,
} from '../../store/modules/navigations';
import {
    actions as actionsV2,
    NAVIGATION_NAMESPACE,
} from '../../store/modules/navigation';

import type { INavigationsItem } from '@gig/magic-sdk/dist/adapter/navigations/getNavigations';
import { Language } from '@gig/magic-sdk/dist/adapter/users/language';
import { NotLocalized } from '@gig/magic-sdk';
import { IMessage } from '@gig/magic-sdk/dist/adapter/messages/message';
import { SweepstakesBalance } from '@gig/magic-sweepstakes';
export interface INormalizedTopNavItem {
    id: string;
    pathId: string;
    label: string;
    icon: string;
    link: string;
    position: number;
    isLoggedInOnly: boolean;
    IsAddDividerBeneath: boolean;
    isNewWindow: boolean;
}

const MAX_TOP_NAV_ITEMS = 6;
const navigationModule = namespace(NAVIGATION_NAMESPACE);
const navigationsModule = namespace(NAVIGATIONS_NAMESPACE);
interface IOptionsOverride {
    shouldShowBack?: boolean;
}

/**
 * The top navigation component
 */
@Component({
    filters: {
        formatCurrency,
    },
    components: {
        SweepstakesBalance,
    },
})
export default class TopNavigation extends Mixins(MagicConfigMixin) {
    RouteName = RouteName;
    /**
     * Emits the action to toggle the menu.
     */
    @navigationModule.Action('toggleMainMenu')
    toggleMenuAction!: OmitFirst<typeof actionsV2.toggleMainMenu>;

    /**
     * Toggles top nav bar show/hide
     */
    @navigationModule.Action('toggleSubTopNavHidden')
    toggleSubTopNavHiddenAction!: OmitFirst<
        typeof actionsV2.toggleSubTopNavHidden
    >;

    /**
     * The menu item to be displayed
     */
    @navigationsModule.Getter('navigations')
    navigations!: ReturnType<typeof getters.navigations>;

    /**
     * Emits an action to fetch the main menu from the
     * API.
     */
    @navigationsModule.Action('fetchAllNavigations')
    fetchAllNavigations!: OmitFirst<typeof actions.fetchAllNavigations>;

    @FeatureEnabledV2({ slug: Feature.LOYALTY_PROGRESS_BAR })
    isLoyaltyProgressEnabled: boolean = false;

    /**
     * is pure pay and play enabled
     */
    @FeatureEnabledV2({
        slug: Feature.PURE_PAY_AND_PLAY,
    })
    isPurePayAndPlayEnabled = false;

    /**
     * is fast track enabled
     */
    @FeatureEnabledV2({
        slug: Feature.FAST_TRACK_INTEGRATION,
    })
    isFastTrackEnabled = false;

    @FeatureEnabledV2({ slug: Feature.CAPTAIN_UP_CHALLENGES })
    isCaptainUpChallengesEnabled: boolean = false;

    @FeatureEnabledV2({ slug: Feature.CAPTAIN_UP_QUEST })
    isCaptainUpQuestEnabled: boolean = false;

    isFastTrackInitialized = false;

    get hasFastTrackConfig(): boolean {
        return !!config.fastTrack?.brandName;
    }

    /**
     * If the navigation bar should use 'light' theme.
     */
    isLight = false;

    /**
     * Whether to show the logo or back button in the left portion of the bar.
     */
    showBack = false;

    /**
     * Whether to show the question mark icon that opens the live chat.
     */
    showHelp = false;

    /**
     * Whether to show the balance widget on light variant.
     */
    showBalance = false;

    /**
     * The translation key for the title to use in the center position of the
     * bar.
     */
    title: string | ((route: Route) => string) = '';

    /**
     * Hide navbar when scrolling down, show when scrolling up
     */
    isHidden: boolean = false;

    /**
     * Number of 'unread' rewards
     */
    rewards: number = 0;

    /**
     * Number of unread messages
     */
    messages = 0;

    /**
     * Whether the user is logged in or not.
     * Shows/hides the balance widget by default.
     */
    isLoggedIn = false;
    hideMenu: boolean = false;
    isMobile: boolean = false;
    isDesktop: boolean = false;
    isLarge: boolean = false;
    isLandscape: boolean = false;
    isTopNavigationEnabled: boolean = false;
    topNavigationSettings: string = '';
    currentLanguage: Language | null = null;
    isSingleLanguage = false;
    lastScrollTop: number = 0;
    isSubTopNavHidden: boolean = false;
    isShadeShown: boolean = true;
    showLeftShade: boolean = false;
    showRightShade: boolean = true;
    showPanicBar: boolean = false;

    private scrollListener!: () => void;
    private previousScrollTop: number = 0;
    private navHeight: number = 0;
    private optionsOverrides: IOptionsOverride = {};
    private overrideNavigationOptionsFnRef:
        | ((event: Event) => void)
        | undefined = undefined;

    private loggedInSubscription: ISubscription | undefined;
    private rewardCountSubscription: ISubscription | undefined;
    private messageSubscription: ISubscription | undefined;
    private deviceSubscription: ISubscription | undefined;
    private isLandscapeSubscription: ISubscription | undefined;
    private currentLocaleSubscription: ISubscription | null = null;
    private showPanicBarSubscription: ISubscription | undefined;
    private fastTrackInitializedSubscription: ISubscription | undefined;

    get isMobileGamePlayView(): boolean {
        return this.$route.name === RouteName.GAMEPLAY && this.isMobile;
    }

    get balanceDelay(): number {
        const isGameplay = this.$route.name === RouteName.GAMEPLAY;
        return isGameplay ? config.gamePlay.balanceDelay : 0;
    }

    get signupTranslation() {
        return this.isPurePayAndPlayEnabled
            ? this.$t('top-navigation.pure-pnp.sign-up')
            : this.$t('top-navigation.sign-up');
    }

    get loginTranslation() {
        return this.isPurePayAndPlayEnabled
            ? this.$t('top-navigation.pure-pnp.log-in')
            : this.$t('top-navigation.login');
    }

    get topNavItems(): INavigationsItem[] {
        const topNavArray: Array<{ [key: string]: INavigationsItem[] }> = [];
        this.navigations?.forEach((navigation) => {
            if (isTopNav(navigation)) {
                topNavArray.push(
                    navigation.items as unknown as {
                        [key: string]: INavigationsItem[];
                    },
                );
            }
        });

        const lang: string = this.magicConfig.language;
        const navigationItems: INavigationsItem[] =
            topNavArray.length > 0 ? topNavArray[0][lang] || [] : [];

        const navigations = navigationItems.map((item) => {
            const data =
                item.label.toLowerCase() === 'rewards'
                    ? { ...item, badge: this.rewards }
                    : item;
            return data;
        });

        const logoutItems = navigations.filter((item) => !item.isLoggedInOnly);

        if (this.isDesktop && !this.isLoggedIn) {
            return logoutItems.length > MAX_TOP_NAV_ITEMS
                ? logoutItems.slice(0, MAX_TOP_NAV_ITEMS)
                : logoutItems;
        }

        if (this.isDesktop && this.isLoggedIn) {
            return navigations.length > MAX_TOP_NAV_ITEMS
                ? navigations.slice(0, MAX_TOP_NAV_ITEMS)
                : navigations;
        }

        if (this.isMobile && !this.isLoggedIn) {
            return logoutItems;
        }

        return navigations;
    }

    get badgeTotal(): number {
        return this.rewards + this.messages;
    }

    get hasLoyaltyProgress(): boolean {
        return !!this.isLoggedIn && this.isLoyaltyProgressEnabled;
    }

    get isGamePage(): boolean {
        return this.$route.name === RouteName.GAMEPLAY;
    }

    get canShowCaptainUpGameplayWidgets(): boolean {
        return (
            this.isGamePage &&
            this.isLoggedIn &&
            !this.isMobileLandscapeGamePlayView &&
            (this.isCaptainUpChallengesEnabled || this.isCaptainUpQuestEnabled)
        );
    }

    get shouldUseCaptainUpGameplayHeader(): boolean {
        return (
            this.isMobileGamePlayView && this.canShowCaptainUpGameplayWidgets
        );
    }

    get isMobileLandscapeGamePlayView(): boolean {
        return this.isMobileGamePlayView && this.isLandscape;
    }

    get isLeftSideMenu(): boolean {
        return this.topNavigationSettings.includes(
            FeatureSettings.LEFT_SIDE_MENU,
        );
    }

    get isBottomNavigationEnabled(): boolean {
        return this.topNavigationSettings.includes(
            FeatureSettings.BOTTOM_NAVIGATION_ENABLED,
        );
    }

    get isSweepstakes(): boolean {
        return !!config.sweepstakes;
    }

    private async updateNavigation(): Promise<void> {
        this.isTopNavigationEnabled = await isTopNavEnabled();
        this.getNavigations();

        if (this.isDesktop) {
            this.showSubTopNavbar();
        } else {
            this.lastScrollTop = 0;
        }
    }

    getLink(currentLanguage: Language, routeName?: string): string {
        if (this.isSingleLanguage && routeName) {
            return routeName;
        }
        let link = `/${currentLanguage}`;
        if (routeName) {
            link = `/${currentLanguage}${routeName}`;
        }

        return link;
    }

    @Throttle(80)
    async handleScroll(): Promise<void> {
        const isStickySubTopNavigationsEnabled: boolean =
            await getFeatureEnabled(Feature.STICKY_SUB_TOP_NAVIGATION);
        if (this.isMobile && !isStickySubTopNavigationsEnabled) {
            this.toggleSubTopNavbar();
        }

        this.toggleShade();
    }

    toggleSubTopNavbar(): void {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        if (scrollTop > this.lastScrollTop) {
            this.hideSubTopNavbar();
        } else if (scrollTop <= this.lastScrollTop) {
            this.showSubTopNavbar();
        }
        this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    }

    hideSubTopNavbar(): void {
        this.isShadeShown = false;
        const navbar = this.$refs.scrollContainer as HTMLElement;
        if (navbar && !this.isSubTopNavHidden) {
            this.toggleSubTopNavHiddenAction();
            document.documentElement.offsetHeight;
            this.isSubTopNavHidden = true;
        }
    }

    showSubTopNavbar(): void {
        this.isShadeShown = true;
        const navbar = this.$refs.scrollContainer as HTMLElement;
        if (navbar && this.isSubTopNavHidden) {
            this.toggleSubTopNavHiddenAction();
            this.isSubTopNavHidden = false;
        }
    }

    public isMobileApp: boolean = false;

    async created(): Promise<void> {
        this.isMobileApp =
            (await window.queryBus.query(IS_MOBILE_APP_QUERY)) ?? false;
        this.deviceSubscription = deviceChange(async () => {
            this.isLarge = await isBreakpointLargeQuery();
            this.isDesktop = await isBreakpointDesktopQuery();
            this.isMobile = await isMobileQuery();
            this.updateNavigation();
        });

        this.isLandscapeSubscription = window.eventBus.subscribe<boolean>(
            BREAKPOINT_LANDSCAPE_UPDATED,
            (event) => {
                this.isLandscape = event.payload ?? false;
            },
            { replay: true },
        );

        await this.updateNavigation();

        this.topNavigationSettings = await getFeatureValueV2(
            Feature.TOP_NAVIGATION,
            '',
        );

        this.loggedInSubscription = window.eventBus.subscribe<boolean>(
            USER_LOGGED_IN_UPDATED,
            (e) => {
                this.isLoggedIn = e.payload ?? false;
            },
            { replay: true },
        );

        this.fastTrackInitializedSubscription =
            window.eventBus.subscribe<boolean>(
                FAST_TRACK_INITIALIZED,
                () => {
                    this.isFastTrackInitialized = true;
                },
                { replay: true },
            );

        this.currentLocaleSubscription = window.eventBus.subscribe<Language>(
            LANGUAGE_UPDATED,
            (e) => {
                this.currentLanguage = e.payload!;
            },
            { replay: true },
        );

        this.rewardCountSubscription = window.eventBus.subscribe<number>(
            REWARD_COUNT,
            (e) => {
                this.rewards = e.payload ?? 0;
            },
            { replay: true },
        );

        this.messageSubscription = window.eventBus.subscribe(
            MESSAGING_LOADED,
            async () => {
                this.messages =
                    (
                        await window.queryBus.query<
                            Array<IMessage<NotLocalized>>
                        >(MESSAGING_UNREAD_LIST)
                    )?.length ?? 0;
            },
            { replay: true },
        );
        (this.showPanicBarSubscription = window.eventBus.subscribe<boolean>(
            PANIC_BAR_VISIBLE,
            (event) => {
                this.showPanicBar = event.payload!;
            },
            { replay: true },
        )),
            (this.isSingleLanguage = await window.queryBus.query(
                SINGLE_LANGUAGE_QUERY,
            ));
    }

    mounted(): void {
        this.overrideNavigationOptionsFnRef =
            this.overrideNavigationOptions.bind(this);
        document.addEventListener(
            'setModalOptions',
            this.overrideNavigationOptionsFnRef,
        );
        window.addEventListener('scroll', this.handleScroll);
    }

    destroyed(): void {
        this.loggedInSubscription?.unsubscribe();
        this.deviceSubscription?.unsubscribe();
        this.isLandscapeSubscription?.unsubscribe();
        this.currentLocaleSubscription?.unsubscribe();
        this.rewardCountSubscription?.unsubscribe();
        this.messageSubscription?.unsubscribe();
        this.showPanicBarSubscription?.unsubscribe();
        this.fastTrackInitializedSubscription?.unsubscribe();

        if (this.scrollListener) {
            this.scrollListener();
        }
        if (!this.overrideNavigationOptionsFnRef) {
            return;
        }
        document.removeEventListener(
            'setModalOptions',
            this.overrideNavigationOptionsFnRef,
        );
        window.removeEventListener('scroll', this.handleScroll);
    }

    toggleOnScroll(): void {
        this.navHeight = this.$el.getBoundingClientRect().height;

        this.scrollListener = scroll.onScroll(this.scrollHandler.bind(this));
    }

    overrideNavigationOptions(event: Event): void {
        const customEvent = event as CustomEvent;
        if (customEvent.detail[0].backArrow === undefined) {
            return;
        }
        this.optionsOverrides = {
            shouldShowBack: customEvent.detail[0].backArrow,
        };
    }

    toggleShade(): void {
        const container = this.$refs.scrollContainer as HTMLElement;
        if (!container) {
            return;
        }
        const scrollLeft = container.scrollLeft;
        const maxScrollLeft = container.scrollWidth - container.clientWidth;

        if (this.isShadeShown && !this.showLeftShade && !this.showRightShade) {
            setTimeout(() => {
                this.showLeftShade = scrollLeft > 0;
                this.showRightShade = scrollLeft < maxScrollLeft - 1;
            }, 30);
        } else if (!this.isShadeShown) {
            this.showLeftShade = false;
            this.showRightShade = false;
        } else {
            this.showLeftShade = scrollLeft > 0;
            this.showRightShade = scrollLeft < maxScrollLeft - 1;
        }
    }

    /**
     * Toggles open/close the menu.
     */
    toggleMenu(event: Event): void {
        event.preventDefault();
        this.toggleMenuAction();
    }

    @Throttle(100)
    scrollHandler(): void {
        const scrollTop = window.scrollY;
        this.isHidden =
            scrollTop > this.previousScrollTop && scrollTop > this.navHeight;
        this.previousScrollTop = scrollTop;
    }

    /**
     * Returns back to the previous URL without
     * refreshing the page.
     */
    goBack(): void {
        this.$router.go(-1);
    }

    openLiveChat(): void {
        openProperLiveChat();
    }

    @Watch('optionsOverrides')
    onOptionsOverrides(): void {
        if (this.optionsOverrides.shouldShowBack !== undefined) {
            this.showBack = this.optionsOverrides.shouldShowBack;
        }
    }

    @Watch('magicConfig.language')
    async getNavigations(): Promise<void> {
        this.fetchAllNavigations({
            isDesktop: !this.isMobile,
            isMobile: this.isMobile,
            langs: [await languageQuery()],
        });
    }

    @Watch('$route', { immediate: true })
    routeChanged(): void {
        const options: ITopNavigationOptions = this.$route.matched.reduce(
            (prev, cur) => ({ ...prev, ...cur.meta.topNavigation }),
            {
                isLight: false,
                showBack: false,
                showHelp: false,
                showBalance: false,
                title: '',
            },
        );

        this.isLight = options.isLight!;
        this.showBack = options.showBack!;
        this.showHelp = options.showHelp!;
        this.showBalance = options.showBalance!;

        if (typeof options.title === 'function') {
            this.title = options.title(this.$route);
        } else {
            this.title = options.title!;
        }

        this.hideMenu = false;
        if (this.$route.name === RouteName.SET_LIMITS) {
            getFeatureEnabled(Feature.SINGLE_DEPOSIT_LIMIT).then((enabled) => {
                if (enabled) {
                    this.title = 'deposit-limit.title';
                    this.showBack = false;
                    this.hideMenu = true;
                }
            });
        }
    }
}
